/// <amd-module name="Artifacts/Feed/hooks/CrossProtocolCommands" />
import { FeedHubActions } from "Artifacts/Feed/redux/Actions";
import * as R from "Artifacts/Feed/Resources";
import { DeleteModalId, IDeleteModalProps } from "Artifacts/Root/components/DeleteModal";
import { DeprecateModalId, IDeprecateModalProps } from "Artifacts/Root/components/DeprecateModal";
import { IPromoteModalProps, PromoteModalId } from "Artifacts/Root/components/PromoteModal";
import {
  IUnlistRelistModalProps,
  UnlistRelistModalId,
} from "Artifacts/Root/components/UnlistRelistModal";
import { UnyankYankModalId } from "Artifacts/Root/components/UnyankYankModal";
import { injectModal } from "Artifacts/Root/protocols/Commands";
import {
  filterSelectedPackages,
  getAggregatedCommands,
  groupPackagesByProtocol,
} from "Artifacts/Root/protocols/CrossProtocolTools";
import * as Tags from "Artifacts/Root/protocols/TagConstants";
import { ICommand } from "Artifacts/Root/protocols/types";
import { getStore } from "Artifacts/Root/Redux";
import { IFeed, IPackageVersionIdentity } from "Artifacts/Root/types";
import { useCallback, useEffect, useState } from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";
import { IListSelection } from "VSSUI/List";

const filterTagSet = new Set<string>([Tags.PackageList]);

/**
 * useCrossProtocolCommands is a hook that enables cross protocol commands based on package list table selection.
 * @param packageData array of package definition and latest version for determining common available commands.
 * @param selection object representing selected package from the package list.
 * @returns list of available commands.
 */
export function useCrossProtocolCommands(
  pkgVersions: IPackageVersionIdentity[] | undefined,
  selection: IListSelection,
  feed: IFeed | undefined,
): {
  commands: ICommand[];
  clearCommands: () => void;
} {
  const { pageContext } = useComponentContext();
  const [commands, setCommands] = useState<ICommand[]>([]);
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const [currentFeedId, setCurrentFeedId] = useState<string | undefined>();
  const [currentViewId, setCurrentViewId] = useState<string | undefined>();

  // TODO: validate if this work with table sorting behavior.
  // onSelect retrieves available commands, aggregates
  // and combines commands with the same id into aggregate command.
  const onSelect = useCallback((): void => {
    if (pkgVersions) {
      const packageMap = groupPackagesByProtocol(selection, pkgVersions);
      const selectedPackages = filterSelectedPackages(selection, pkgVersions);

      const promoteModalInjector = injectModal<IPromoteModalProps>(
        pageContext,
        "promote",
        PromoteModalId,
        (command, hideModal) => ({
          packages: selectedPackages,
          onPromote: (viewId: string) => {
            return () => {
              command.action({
                feed,
                selectedPackages,
                viewId,
                onSuccessCallback: (
                  selectedPackages: IPackageVersionIdentity[],
                  viewName?: string,
                ) => {
                  viewName &&
                    dispatch(FeedHubActions.fakePackagePromote(selectedPackages, viewName));
                  announce(
                    getAnnouncement(R.PackageCommand_Announcement_Promoted, selectedPackages),
                  );
                  setCommands([]);
                  selection.clear();
                  hideModal();
                },
              });
            };
          },
          onDismiss: () => {
            hideModal();
          },
        }),
      );

      const deleteModalInjector = injectModal<IDeleteModalProps>(
        pageContext,
        "delete",
        DeleteModalId,
        (command, hideModal) => ({
          packages: selectedPackages,
          onDelete: () => {
            command.action({
              feed,
              selectedPackages,
              onSuccessCallback: (selectedPackages: IPackageVersionIdentity[]) => {
                dispatch(FeedHubActions.fakePackageDelete(selectedPackages));
                announce(getAnnouncement(R.PackageCommand_Announcement_Deleted, selectedPackages));
                setCommands([]);
                selection.clear();
                hideModal();
              },
            });
          },
          onDismiss: hideModal,
        }),
      );

      const unlistRelistModalInjector = injectModal<IUnlistRelistModalProps>(
        pageContext,
        "unlistRelist",
        UnlistRelistModalId,
        (command, hideModal) => ({
          packages: selectedPackages,
          onUnlistRelist: () => {
            command.action({
              feed,
              selectedPackages,
              onSuccessCallback: (
                selectedPackages: IPackageVersionIdentity[],
                isListed: boolean,
              ) => {
                dispatch(FeedHubActions.fakePackageListUnlist(selectedPackages, isListed));
                if (isListed) {
                  announce(
                    getAnnouncement(R.PackageCommand_Announcement_Relisted, selectedPackages),
                  );
                } else {
                  announce(
                    getAnnouncement(R.PackageCommand_Announcement_Unlisted, selectedPackages),
                  );
                }
                setCommands([]);
                selection.clear();
                hideModal();
              },
            });
          },
          onDismiss: hideModal,
        }),
      );

      const unyankYankModalInjector = injectModal<IUnlistRelistModalProps>(
        pageContext,
        "unyankYank",
        UnyankYankModalId,
        (command, hideModal) => ({
          packages: selectedPackages,
          onUnlistRelist: () => {
            command.action({
              feed,
              selectedPackages,
              onSuccessCallback: (
                selectedPackages: IPackageVersionIdentity[],
                isListed: boolean,
              ) => {
                dispatch(FeedHubActions.fakePackageListUnlist(selectedPackages, isListed));
                if (isListed) {
                  announce(
                    getAnnouncement(R.PackageCommand_Announcement_Relisted, selectedPackages),
                  );
                } else {
                  announce(
                    getAnnouncement(R.PackageCommand_Announcement_Unlisted, selectedPackages),
                  );
                }
                setCommands([]);
                selection.clear();
                hideModal();
              },
            });
          },
          onDismiss: hideModal,
        }),
      );

      const deprecateModalInjector = injectModal<IDeprecateModalProps>(
        pageContext,
        "deprecate",
        DeprecateModalId,
        (command, hideModal) => ({
          packages: selectedPackages,
          onDeprecate: (deprecateMessage: string) => {
            command.action({
              feed,
              selectedPackages,
              deprecateMessage,
              onSuccessCallback: () => {
                announce(
                  getAnnouncement(R.PackageCommand_Announcement_Deprecated, selectedPackages),
                );
                setCommands([]);
                selection.clear();
                hideModal();
              },
            });
          },
          onDismiss: hideModal,
        }),
      );

      const aggregatedCommands = deprecateModalInjector(
        unlistRelistModalInjector(
          unyankYankModalInjector(
            deleteModalInjector(
              promoteModalInjector(
                getAggregatedCommands(pageContext, packageMap, feed, filterTagSet),
              ),
            ),
          ),
        ),
      );
      setCommands(aggregatedCommands);
    }
  }, [pkgVersions, selection, feed, pageContext, dispatch]);

  useEffect(() => {
    if (feed && (feed.id !== currentFeedId || feed.selectedViewId !== currentViewId)) {
      setCurrentFeedId(feed.id);
      setCurrentViewId(feed.selectedViewId);
      setCommands([]);
      selection.clear();
    }

    selection.subscribe(onSelect);
    return () => {
      selection.unsubscribe(onSelect);
    };
  }, [onSelect, feed, selection, setCommands, currentFeedId, currentViewId]);

  const clearCommands = useCallback(() => setCommands([]), [setCommands]);

  return {
    commands,
    clearCommands,
  };
}

function getAnnouncement(
  protocolResourceString: string,
  selectedPackages: IPackageVersionIdentity[],
): string {
  if (selectedPackages.length > 1) {
    return Utils_String.format(
      R.PackageCommand_Announcement_Multiple,
      selectedPackages.length,
      protocolResourceString,
    );
  } else {
    return Utils_String.format(
      R.PackageCommand_Announcement,
      selectedPackages[0].name,
      selectedPackages[0].version,
      protocolResourceString,
    );
  }
}
